type ScrollToCenterOptions = {
  behavior?: ScrollBehavior;
  /**
   * Extra offset applied to the final scrollTop (positive moves content up).
   * Useful when you have sticky footers/headers overlapping the scrollport.
   */
  extraOffsetPx?: number;
};

const getPreferredScrollBehavior = (behavior?: ScrollBehavior): ScrollBehavior => {
  const requested = behavior ?? "auto";
  if (typeof window === "undefined") return requested;

  // Respect OS/browser accessibility settings.
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  return prefersReducedMotion ? "auto" : requested;
};

const isScrollableY = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;
  return (
    (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
    element.scrollHeight > element.clientHeight + 1
  );
};

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    if (isScrollableY(parent)) return parent;
    parent = parent.parentElement;
  }

  return (document.scrollingElement as HTMLElement | null) ?? null;
};

const scrollToTop = (container: HTMLElement, top: number, behavior: ScrollBehavior) => {
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const nextTop = Math.max(0, Math.min(top, maxTop));

  // Some engines may throw on unsupported options object.
  try {
    container.scrollTo({ top: nextTop, behavior });
  } catch {
    container.scrollTop = nextTop;
  }
};

export const focusWithoutScroll = (element: HTMLElement) => {
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
};

export const scrollElementToCenter = (
  element: HTMLElement,
  options: ScrollToCenterOptions = {},
) => {
  if (typeof window === "undefined") return;
  if (!element.isConnected) return;

  const behavior = getPreferredScrollBehavior(options.behavior);
  const extraOffsetPx = options.extraOffsetPx ?? 0;

  const container = getScrollParent(element);
  if (!container) {
    // Last resort: let the browser decide.
    element.scrollIntoView({ behavior, block: "center" });
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  const elementTopInContainer = elementRect.top - containerRect.top + container.scrollTop;
  const targetTop =
    elementTopInContainer - (container.clientHeight / 2 - elementRect.height / 2) - extraOffsetPx;

  scrollToTop(container, targetTop, behavior);
};

export const focusAndScrollToCenter = (
  element: HTMLElement,
  options: ScrollToCenterOptions = {},
) => {
  if (typeof window === "undefined") return;
  if (!element.isConnected) return;

  focusWithoutScroll(element);

  // Safari/WebKit often settles focus/layout asynchronously; double-rAF is the most reliable.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollElementToCenter(element, options);
    });
  });
};
